import { Stack, Typography, useTheme } from "@mui/material";

import { useResponsive } from "@app/hooks";

interface FeatureItem {
  img: string;
  title: string;
  desc: string;
}

interface FeaturesSectionProps {
  heading: string;
  subHeading?: string;
  features: FeatureItem[];
  rightImage: string;
}

const FeaturesCards = ({
  heading,
  subHeading,
  features,
  rightImage,
}: FeaturesSectionProps) => {
  const isMobile = useResponsive("down", "md");
  const theme = useTheme();

  return (
    <Stack
      direction={{ xs: "column", md: "row" }}
      alignItems="center"
      spacing={4}
      sx={{
        py: 6,
        px: 10,
        alignSelf: "center",
        width: { xs: "100%" },
      }}
      marginY={isMobile ? 0 : 5}
      position={"relative"}
    >
      <Stack
        maxWidth={{ xs: "100%", sm: "80%" }}
        sx={{
          marginRight: isMobile ? 0 : 5,
          ml: isMobile ? 0 : 4,
          alignItems: isMobile ? "center" : "flex-start",
          justifyContent: isMobile ? "center" : "flex-start",
        }}
      >
        <Typography
          gutterBottom
          fontWeight="bold"
          sx={{
            backgroundColor: theme.palette.primary.lighter,
            p: 2,
            borderRadius: 1,
            fontFamily: "Work Sans",
            fontWeight: "500",
            mx: isMobile ? "auto" : 0,
            textTransform: "uppercase",
            alignSelf: isMobile ? "center" : "flex-start",
            color: theme.palette.primary.main,
          }}
        >
          {heading}
        </Typography>

        <Typography
          variant="h5"
          fontWeight="bold"
          gutterBottom
          sx={{
            fontSize: { xs: 20, sm: 24 },
            fontFamily: "Work Sans",
            fontWeight: "700",
            width: isMobile ? "100%" : "78%",
          }}
        >
          {subHeading}
        </Typography>

        <Stack spacing={2} mt={2}>
          {features?.map((item, index) => (
            <Stack
              direction="row"
              alignItems="flex-start"
              spacing={1}
              key={index}
            >
              <Stack
                sx={{
                  background: theme.palette.primary.lighter,
                  width: 49,
                  padding: 2,
                  borderRadius: 1,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: isMobile ? "auto" : 0,
                }}
              >
                <img
                  src={item.img}
                  loading="lazy"
                  alt={item.title}
                  style={{
                    height: "100%",
                    maxWidth: "100%",
                    objectFit: "contain",
                  }}
                />
              </Stack>
              <Stack>
                <Typography
                  fontWeight="bold"
                  sx={{
                    fontFamily: "Work Sans",
                    fontWeight: "500",
                    color: theme.palette.common.black,
                  }}
                >
                  {item.title}
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    fontFamily: "Work Sans",
                    fontWeight: "500",
                    color: theme.palette.grey[300],
                    width: isMobile ? "100%" : "80%",
                    mx: isMobile ? "auto" : 0,
                  }}
                >
                  {item.desc}
                </Typography>
              </Stack>
            </Stack>
          ))}
        </Stack>
      </Stack>

      {!isMobile && (
        <Stack
          sx={{
            alignItems: "center",
            justifyContent: "center",
            pr: 0,
            position: "absolute",
            right: { md: -255, lg: 0, xl: -50 },
            zIndex: -1,
          }}
        >
          <img
            src={rightImage}
            alt="features"
            style={{
              maxWidth: 800,
              height: "auto",
              objectFit: "contain",
            }}
            loading="lazy"
          />
        </Stack>
      )}
    </Stack>
  );
};

export { FeaturesCards };
